import { useEffect, useRef, useState } from "react";
import {
  LEVERAGE_DEFAULT,
  LEVERAGE_MAX,
  LEVERAGE_MIN,
} from "../constants";
import { clampLeverage } from "../utils/leverage";

/**
 * Leverage text-field draft, focus/blur, and slider apply bridge.
 *
 * @param {object} params
 * @param {number} params.leverage
 * @param {(value: number) => void} params.onLeverageChange
 */
export const useLeverageInput = ({ leverage, onLeverageChange }) => {
  const [leverageDraft, setLeverageDraft] = useState(String(LEVERAGE_DEFAULT));
  const leverageFocusedRef = useRef(false);
  const leverageInitializedRef = useRef(false);
  const leverageInputRef = useRef(null);
  const skipLeverageBlurCommitRef = useRef(false);

  useEffect(() => {
    if (!leverageInitializedRef.current) {
      leverageInitializedRef.current = true;
      setLeverageDraft(String(LEVERAGE_DEFAULT));
      onLeverageChange(LEVERAGE_DEFAULT);
      return;
    }

    if (leverageFocusedRef.current) return;

    const numeric = Math.trunc(Number(leverage));

    if (!Number.isFinite(numeric)) return;

    setLeverageDraft(String(clampLeverage(numeric)));
  }, [leverage, onLeverageChange]);

  const onLeverageInput = (event) => {
    const digits = event.target.value.replace(/\D/g, "").slice(0, 4);

    setLeverageDraft(digits);

    const numeric = Number(digits);

    if (digits !== "" && numeric >= LEVERAGE_MIN && numeric <= LEVERAGE_MAX) {
      onLeverageChange(numeric);
    }
  };

  const onLeverageFocus = (event) => {
    leverageFocusedRef.current = true;
    event.target.select();
  };

  const onLeverageBlur = () => {
    const shouldSkip = skipLeverageBlurCommitRef.current;
    skipLeverageBlurCommitRef.current = false;
    leverageFocusedRef.current = false;

    if (shouldSkip) return;

    const numeric = Number(leverageDraft);
    let next = LEVERAGE_MIN;

    if (leverageDraft !== "" && Number.isFinite(numeric)) {
      next = clampLeverage(numeric);
    }

    setLeverageDraft(String(next));
    onLeverageChange(next);
  };

  const applyLeverageFromSlider = (next) => {
    leverageFocusedRef.current = false;
    setLeverageDraft(String(next));
    onLeverageChange(next);

    if (document.activeElement === leverageInputRef.current) {
      skipLeverageBlurCommitRef.current = true;
      leverageInputRef.current.blur();
    }
  };

  return {
    leverageDraft,
    leverageInputRef,
    onLeverageInput,
    onLeverageFocus,
    onLeverageBlur,
    applyLeverageFromSlider,
  };
};
